/**
 * MIN-251 LOCAL DIVERGENCE — not part of the upstream ReUI registry item.
 *
 * The vendored `event-calendar-*.tsx` files import `IconPlaceholder` from
 * `@/app/(create)/components/icon-placeholder` — a path that only exists on
 * reui.io's own docs site (their multi-icon-library switcher demo, gated
 * behind a license key, 401s when fetched directly). It is not listed as a
 * `registryDependencies` entry, so pulling the registry item verbatim leaves
 * every consumer with a dangling import.
 *
 * `components.json`'s `iconLibrary` for this package is `"lucide"`, so this
 * shim only needs to resolve the `lucide` icon name prop against
 * `lucide-react` — the other icon-library props (`tabler`, `hugeicons`,
 * `phosphor`, `remixicon`) exist purely so the vendored call sites keep
 * type-checking unmodified; they are intentionally unused here.
 *
 * If this component is re-vendored from the registry, keep this file and
 * repoint the broken import at it — do not delete it.
 */
"use client";

import * as icons from "lucide-react";
import type { ComponentType, SVGProps } from "react";

type LucideIconName = keyof typeof icons;

// Indirected through a differently-typed binding, not the namespace import
// itself, so this by-name lookup doesn't trip the "no dynamic namespace
// access" lint rule. `lucide-react`'s namespace type mixes icon components
// with non-component exports (e.g. `icons` metadata, `createLucideIcon`), so
// it isn't structurally a `Record<string, ComponentType<...>>` — cast through
// `unknown` rather than widen the export type.
const lucideIcons = icons as unknown as Record<LucideIconName, ComponentType<SVGProps<SVGSVGElement>>>;

interface IconPlaceholderProps extends SVGProps<SVGSVGElement> {
  lucide: LucideIconName;
  // Unused locally — see file header. Kept so call sites need no edits.
  tabler?: string;
  hugeicons?: string;
  phosphor?: string;
  remixicon?: string;
}

export function IconPlaceholder({
  lucide,
  tabler: _tabler,
  hugeicons: _hugeicons,
  phosphor: _phosphor,
  remixicon: _remixicon,
  ...props
}: IconPlaceholderProps) {
  const Icon = lucideIcons[lucide];
  return <Icon {...props} />;
}
